import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Link2, Mail, ShieldCheck } from 'lucide-react'
import { articleBlurb, type Article } from '@/lib/api'
import { getLocalArticle } from '@/data/articles'
import { usePageSeo } from '@/lib/page-seo'
import { resolveSite, siteBrand } from '@/lib/site'
import { useLocale } from '@/lib/locale'
import { useUi } from '@/lib/i18n/ui'
import { QATAR_BRAND } from '@/components/qatar/brand'

export const Route = createFileRoute('/articles/$slug')({
  component: ArticlePage,
})

type TocEntry = { id: string; text: string; level: 2 | 3 }

function ArticlePage() {
  const { slug } = Route.useParams()
  const source = getLocalArticle(slug)
  const locale = useLocale()
  const t = useUi()
  const isQatar = resolveSite() === 'qatar'
  const publisher = isQatar ? QATAR_BRAND.program[locale] : siteBrand().name
  // Arabic swaps title/blurb/body in place; everything else (image, read
  // time, tags) is shared.
  const data: Article | null =
    source && locale === 'ar' && source.ar
      ? { ...source, title: source.ar.title, description: source.ar.description ?? source.description, excerpt: null, content: source.ar.content ?? source.content }
      : source
  const isError = data === null

  usePageSeo(
    data
      ? {
          title: `${data.title} · ${publisher}`,
          description: articleBlurb(data) ?? undefined,
          canonicalPath: `/articles/${slug}`,
        }
      : null,
  )

  const [toc, setToc] = useState<TocEntry[]>([])
  const [renderedContent, setRenderedContent] = useState<string>('')
  const [copied, setCopied] = useState(false)

  // Parse the content once it arrives: assign deterministic IDs to every
  // heading, build a table-of-contents list for the sidebar, and feed the
  // mutated HTML back into the article body so anchor scrolls land on the
  // right element.
  useEffect(() => {
    if (!data?.content || typeof window === 'undefined') {
      setRenderedContent(data?.content ?? '')
      setToc([])
      return
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(data.content, 'text/html')
    const headingElements = Array.from(doc.querySelectorAll('h1, h2, h3'))
    const seen = new Set<string>()

    const slugify = (text: string) =>
      text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')

    const headings = headingElements.map((el, idx) => {
      const text = el.textContent?.trim() || `Section ${idx + 1}`
      let id = slugify(text) || `section-${idx + 1}`
      let counter = 1
      while (seen.has(id)) {
        id = `${id}-${counter++}`
      }
      seen.add(id)
      el.id = id
      return {
        id,
        text,
        level: el.tagName === 'H3' ? 3 : 2,
      } as const satisfies TocEntry
    })

    setToc(headings)
    setRenderedContent(doc.body.innerHTML)
  }, [data?.content])

  const pageUrl = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return window.location.href
  }, [slug, data?.title])

  const handleCopyLink = async () => {
    if (typeof navigator === 'undefined') return
    try {
      await navigator.clipboard.writeText(pageUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore copy failures
    }
  }

  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const tags = data?.tags ?? []
  const primaryTag = tags[0]?.name ?? data?.category?.name ?? null

  const emailHref = `mailto:?subject=${encodeURIComponent(
    data?.title ?? publisher,
  )}&body=${encodeURIComponent(pageUrl)}`

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-6xl px-6 pb-20 pt-6 sm:pb-24 lg:pt-8">
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 text-base font-semibold text-violet-600 hover:text-violet-700"
        >
          <ArrowLeft className="h-5 w-5" />
          {t.backToArticles}
        </Link>

        {isError && (
          <p className="mt-12 text-sm text-slate-500">
            {t.articleNotFound}{' '}
            <Link to="/articles" className="font-semibold text-violet-600">
              {t.browseAllArticles}
            </Link>
          </p>
        )}

        {data && (
          <>
            {/* ── Header ─────────────────────────────────── */}
            <header className="mt-6 space-y-4 text-center sm:mt-8">
              {tags.length > 0 ? (
                <div className="flex flex-wrap justify-center gap-2">
                  {tags.map(t => (
                    <span
                      key={t.slug}
                      className="inline-flex items-center rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-violet-700"
                    >
                      {t.name}
                    </span>
                  ))}
                </div>
              ) : primaryTag ? (
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                  {primaryTag}
                </p>
              ) : null}

              <h1 className="text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
                {data.title}
              </h1>

              {articleBlurb(data) && (
                <p className="mx-auto max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
                  {articleBlurb(data)}
                </p>
              )}
            </header>

            {/* ── Banner ─────────────────────────────────── */}
            {data.banner && (
              <div className="mt-10 overflow-hidden rounded-2xl border border-slate-100 shadow-sm">
                <img
                  src={data.banner}
                  alt={data.title}
                  loading="lazy"
                  className="max-h-[520px] w-full object-cover"
                />
              </div>
            )}

            {/* ── Meta + share strip ─────────────────────── */}
            <div className="mt-10 flex flex-col gap-4 border-y border-slate-200 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-500 text-white shadow-sm">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-slate-900">
                    {publisher}
                  </p>
                  {data.readTime && (
                    <p className="text-xs text-slate-500">
                      {t.minRead(data.readTime)}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-600 sm:flex-shrink-0">
                <button
                  onClick={handleCopyLink}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 transition hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
                  aria-label={t.copyLink}
                >
                  <Link2 className="h-4 w-4" />
                  {copied ? t.copied : t.copyLink}
                </button>
                <a
                  href={emailHref}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
                  aria-label={t.shareEmail}
                >
                  <Mail className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* ── Body: TOC sidebar + content ────────────── */}
            <div className="mt-10 grid items-start gap-10 lg:grid-cols-12 lg:gap-14">
              {toc.length > 0 && (
                <aside className="hidden self-start lg:sticky lg:top-24 lg:col-span-4 lg:block">
                  <div className="rounded-2xl">
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t.contents}
                    </h3>
                    <div className="max-h-[420px] space-y-0 overflow-y-auto border-s border-slate-200 pe-1">
                      {toc.map(item => (
                        <button
                          key={item.id}
                          onClick={() => handleScrollTo(item.id)}
                          className={`block w-full cursor-pointer rounded-lg px-3 py-2 text-start text-sm transition hover:bg-violet-50 hover:text-violet-700 ${
                            item.level === 3
                              ? 'ps-5 text-slate-500'
                              : 'font-medium text-slate-800'
                          }`}
                        >
                          {item.text}
                        </button>
                      ))}
                    </div>
                  </div>
                </aside>
              )}

              <article
                className={
                  toc.length > 0
                    ? 'min-w-0 lg:col-span-8'
                    : 'min-w-0 lg:col-span-12'
                }
              >
                {data.content && (
                  <div
                    className="prose prose-slate prose-lg max-w-none prose-headings:scroll-mt-28 prose-headings:tracking-tight prose-headings:text-slate-900 prose-p:leading-relaxed prose-p:text-slate-700 prose-a:text-violet-700 prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-violet-800 prose-strong:text-slate-900 prose-blockquote:rounded-xl prose-blockquote:border-s-4 prose-blockquote:border-violet-200 prose-blockquote:bg-violet-50 prose-blockquote:px-5 prose-blockquote:py-4 prose-blockquote:text-slate-700 prose-img:rounded-2xl prose-img:shadow-sm prose-hr:border-slate-200"
                    dangerouslySetInnerHTML={{
                      __html: renderedContent || data.content,
                    }}
                  />
                )}
              </article>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
